import {
  LocalizedEventData,
  SupportedLanguages,
  useListLocalizedEventCategories,
  useListPaginatedLocalizedEvents,
} from '@carlito/bookito-client';
import { cssModuleClassNames } from '@carlito/css';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import BookitoEventDetail from '../BookitoEventDetail';
import EmptyCard from '../Card/EmptyCard';
import ExperienceCardHolder from '../CardHolder';
import Modal from '../Modal';
import CarlitoLogo from '../assets/carlito-logo.svg';
import styles from '../index.module.css';
import { BookitoWidgetViewMode } from '../types';
const { getCssModuleClassName } = cssModuleClassNames(styles);

export type BookitoWidgetProps = {
  authId: string;
  categoryId?: string;
  title?: string;
  language?: SupportedLanguages;
  viewMode?: BookitoWidgetViewMode;
  excludeNonBookable?: boolean;
};

type SelectedEvent = { event: LocalizedEventData; date: Date };

const dateToday = new Date();
dateToday.setHours(0, 0, 0, 0);

const BookitoWidget = (props: BookitoWidgetProps) => {
  const { viewMode = 'Date' } = props;
  const { i18n } = useTranslation();
  const [canFetchCategoryList, setFetchingCategoryList] = useState(false);
  const [categoryTitle, setCategoryTitle] = useState('');

  useLayoutEffect(() => {
    if (props?.language) {
      i18n.changeLanguage(props?.language);
    }
  }, [props?.language, i18n]);

  const onCloseModal = () => {
    setSelectedEvent(undefined);
  };

  const [selectedEvent, setSelectedEvent] = useState<SelectedEvent>();

  const EmptyEventsList = () => {
    return (
      <>
        {Array(10)
          .fill(undefined)
          .map((_, key) => (
            <EmptyCard key={key} />
          ))}
      </>
    );
  };

  const listEventsQuery = useListPaginatedLocalizedEvents({
    fromDate: dateToday.toISOString(),
    excludeCancelled: true,
    includeHidden: false,
    authId: props?.authId,
    categoryId: props?.categoryId,
    excludeNonBookable: props.excludeNonBookable,
  });

  const listEventCategoriesQuery = useListLocalizedEventCategories(
    { authId: props?.authId },
    canFetchCategoryList
  );

  const isListLoadingOrEmpty =
    listEventsQuery.isLoading ||
    (listEventCategoriesQuery.isLoading && canFetchCategoryList) ||
    (listEventsQuery.isSuccess && listEventsQuery.data.pages.length === 0);

  useEffect(() => {
    if (!props.title && props.categoryId) {
      setFetchingCategoryList(true);
    }
  }, [props.title, props.categoryId]);

  const eventCategories = listEventCategoriesQuery.data?.eventCategories;

  useEffect(() => {
    if (eventCategories) {
      const categoryId = props.categoryId;

      const eventCategory = eventCategories.find(({ id }: { id: string }) => id === categoryId);

      if (eventCategory) {
        const title = eventCategory.displayName;
        if (title) {
          setCategoryTitle(title);
        }
      }
    }
  }, [eventCategories, i18n.language, props.categoryId]);

  const allPages = listEventsQuery.data?.pages;

  const getAvailabilityDates = (eventId: string) => {
    return (
      (allPages &&
        allPages
          .flatMap((page) => {
            return Object.entries(page.dates).map(([dateKey, values]) => {
              const isEventOnDate = values.events.filter(({ id }) => eventId === id);

              if (isEventOnDate.length) {
                return dateKey;
              }

              return '';
            });
          })
          ?.filter((el) => !!el)) ||
      []
    );
  };

  const ListView = ({ pages }: { pages: any }) => {
    return pages.map((page: any) => {
      const events = page ? page.events : [];
      return (
        <ExperienceCardHolder
          onDate={dateToday}
          events={events}
          openModal={(event) => setSelectedEvent({ event, date: dateToday })}
        />
      );
    });
  };

  function uppercaseFirstLetter(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  const DateView = ({ pages }: { pages: any }) => {
    return pages.map((page: any) => {
      const sortedDates = page
        ? Object.entries(page.dates).sort(([a], [b]) => {
            const aDate = new Date(a);
            const bDate = new Date(b);
            return aDate.getTime() - bDate.getTime();
          })
        : [];

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      return sortedDates.map(([dateStr, { events }], idx) => {
        const eventsToRender = events.map((dateEntry: any) => {
          const foundEvent = page.events.find(
            (pageEv: any) => pageEv.id === dateEntry.id
          ) as LocalizedEventData;
          return foundEvent;
        });

        if (eventsToRender.length === 0) {
          return null;
        }

        const date = new Date(dateStr);

        return (
          <div key={date.toISOString()}>
            <span className="bookito-widget__events-date text-lg font-bold ml-4 mb-6 !text-left">
              {uppercaseFirstLetter(
                date.toLocaleDateString(i18n.language, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })
              )}
            </span>
            <ExperienceCardHolder
              onDate={date}
              events={eventsToRender}
              openModal={(event) => setSelectedEvent({ event, date })}
            />
          </div>
        );
      });
    });
  };

  return (
    <div className="bookito-widget mx-8">
      <div className="bookito-widget__title font-extrabold text-4xl non-italic">
        {props?.title || categoryTitle || <Trans i18nKey="weekly-programs"></Trans>}
      </div>
      <div className="mb-10 p-2 ">
        <div
          className={classNames(
            'pb-6 flex flex-initial flex- max-w-screen overflow-x-auto',
            getCssModuleClassName('custom-scrollbar')
          )}
        >
          {isListLoadingOrEmpty && <EmptyEventsList />}

          {listEventsQuery.isSuccess && allPages && viewMode === 'Date' && (
            <DateView pages={allPages} />
          )}

          {listEventsQuery.isSuccess && allPages && viewMode === 'List' && (
            <ListView pages={allPages} />
          )}
        </div>
        <div className="ml-auto min-w-min flex justify-end items-center opacity-30 pt-1">
          <p className="text-sm mr-2">powered by</p>
          <a href={'https://carlito-app.com'} target={'__blank'}>
            <img src={CarlitoLogo} alt="logo" width={'80px'} />
          </a>
        </div>
      </div>

      {selectedEvent && (
        <Modal onCloseModal={onCloseModal}>
          <BookitoEventDetail
            event={selectedEvent.event}
            date={selectedEvent.date}
            authId={props?.authId}
            availableOnDates={getAvailabilityDates(selectedEvent.event.id)}
            viewMode={viewMode}
          />
        </Modal>
      )}
    </div>
  );
};
export default BookitoWidget;
